// Trend: stacked uPlot lanes on one process-time axis + digital rows + step band + event flags.
import { SIGNAL_INDEX, STATION_DEFS, PHASES } from "./sim.js"
import { createEventLayer, DETAIL_MAX_S } from "./chart-events.js"
export { placeSpans, spanPaint, placePill, stackPills, chainPolylines, chainEdges, keepChains, pillLabel, visibleEvents, laneOf, pillWidth,
  SEVERITY_FILL, TAG_GUTTER } from "./chart-events.js"

export const WINDOWS = [120, 900, 3600, 28800]
export const WINDOW_LABELS = { 120: "2 min", 900: "15 min", 3600: "1 h", 28800: "8 h" }
const RENDER_MS = 250
const RENDER_MS_INTERACT = 50   // floor while the user pans / changes window (a redraw was requested)

const st = (k) => STATION_DEFS.map((d) => ({ key: `${d.id}.${k}`, label: d.id, color: d.color }))
export const LANES = [
  { id: "wt",    title: "Hopper weight",   axis: "Weight (kg)",   unit: "kg",   range: [0, 140],  ticks: [0, 25, 50, 75, 100, 125], dec: 1, series: st("wt"),      setpoints: STATION_DEFS.map((d) => `${d.id}.sp`), height: 170,
    descriptor: "Six doses by recipe; dashed ticks are setpoints; the verify ends each dose" },
  { id: "flow",  title: "Ingredient feed", axis: "Feed (kg/s)",   unit: "kg/s", range: [0, 2],    ticks: [0, 0.5, 1, 1.5, 2],       dec: 2, series: st("flow_in"), setpoints: [], height: 130,
    descriptor: "Fast feed, then dribble, then the valve closes early by the preact" },
  { id: "tank",  title: "TK-200",          axis: "Level (%)", axis2: "Flow (kg/s)", unit: "%", range: [0, 100], ticks: [0, 25, 50, 75, 100], range2: [0, 20], ticks2: [0, 5, 10, 15, 20], dec: 1,
    series: [{ key: "TK-200.level_pct", label: "Level", color: "#f59e0b" },
      { key: "TK-200.flow_in", label: "Inflow", color: "#10b981", scale: "kgs", unit: "kg/s" }, { key: "TK-200.flow_out", label: "Dispense", color: "#38bdf8", scale: "kgs", unit: "kg/s" }], setpoints: [], height: 110,
    descriptor: "Level saws between batches; transfers need < 72 %" },
  { id: "rpm",   title: "Agitators",       axis: "Speed (rpm)",   unit: "rpm",  range: [0, 1500], ticks: [0, 500, 1000, 1500],       dec: 0, series: st("rpm"),     setpoints: [], height: 130,
    descriptor: "High-shear mix after every dose" },
  { id: "shock", title: "Shock envelope",  axis: "Shock (g)",     unit: "g",    range: [0, 6],    ticks: [0, 1, 2, 3, 4, 5, 6], dec: 1, limit: { value: 3.2, label: "3.2 g limit" },
    series: [...st("shock_pk"), ...st("plc_rms").map((s) => ({ ...s, label: s.label + " PLC", dash: [4, 4], color: "#94a3b8", plc: true }))], setpoints: [], height: 130,
    descriptor: "Edge peak vs the controller's scan; 3.2 g is the trip" },
]

/** Vertical tag placement. `values` are desired positions in px measured UP from the bottom of a box `height` px tall;
 *  returns tops (px DOWN from the box top) in input order, pairwise >= minGap apart, all inside [0, height]. */
export function tagLayout(values, height, minGap = 12) {
  const n = values.length
  if (!n) return []
  const want = values.map((v) => Math.min(height, Math.max(0, height - v)))
  const order = want.map((_, i) => i).sort((a, b) => want[a] - want[b] || a - b)
  const y = order.map((i) => want[i])
  for (let k = 1; k < n; k++) if (y[k] < y[k - 1] + minGap) y[k] = y[k - 1] + minGap
  if (y[n - 1] > height) {                       // ran off the bottom: push back up from the end
    y[n - 1] = height
    for (let k = n - 2; k >= 0; k--) if (y[k] > y[k + 1] - minGap) y[k] = y[k + 1] - minGap
  }
  const out = new Array(n)
  order.forEach((i, k) => { out[i] = Math.max(0, y[k]) })
  return out
}
/** What the trend draws at a given window length: markers/pills thin out as the window grows. */
export function densityFor(windowSeconds) {
  return { batchMarkers: windowSeconds <= 3600, pills: windowSeconds <= DETAIL_MAX_S, perValveRows: windowSeconds <= 900 }
}
export const PHASE_COLORS = { IDLE: "#1f2a3d", TARE: "#334155", FAST: "#0ea5e9", DRIBBLE: "#f59e0b", SETTLE: "#64748b", VERIFY: "#a3e635",
  MIX_RAMP: "#8b5cf6", MIX_HOLD: "#7c3aed", MIX_COAST: "#5b21b6", WAIT_TRANSFER: "#b45309", DRAIN: "#10b981", EMPTY: "#065f46" }

export function valveState(xv01, xv02) { return xv01 > 0.5 ? 2 : xv02 > 0.5 ? 1 : 0 }

function hex2rgba(hex, a) { const n = parseInt(hex.slice(1), 16); return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})` }
/** Envelope bands for a lane: series are laid out [x, min0, max0, min1, max1, ...]; a band fills max -> min. Static per lane. */
function laneBands(lane) {
  return lane.series.map((s, i) => ({ series: [2 + i * 2, 1 + i * 2], fill: hex2rgba(s.color, 0.18) }))
}

/** uPlot data for one lane: [ts, (min,max) per series..., setpoint per station...] and the band pairs. */
export function buildLaneData(history, lane, from, to, px) {
  const n = lane.series.length
  const idx = [...lane.series.map((s) => SIGNAL_INDEX[s.key]), ...lane.setpoints.map((k) => SIGNAL_INDEX[k])]
  const all = history.queryMany(idx, from, to, px)   // one pass over the tier for the whole lane
  const qs = all.slice(0, n), sps = all.slice(n)
  // Align on the first series' timestamps (all tiers share bucket edges).
  const ts = qs[0].ts
  const data = [ts]
  qs.forEach((q) => { data.push(alignTo(ts, q.ts, q.min)); data.push(alignTo(ts, q.ts, q.max)) })
  for (const q of sps) data.push(alignTo(ts, q.ts, q.last))
  return { data, bands: laneBands(lane), n }
}
// Missing points become null (a gap): uPlot's autorange skips null but NaN can poison it.
function alignTo(ts, qts, vals) {
  if (qts.length === ts.length) return vals
  const out = new Array(ts.length).fill(null)
  let j = 0
  for (let i = 0; i < ts.length; i++) { while (j < qts.length - 1 && qts[j + 1] <= ts[i]) j++; if (Math.abs(qts[j] - ts[i]) < 1e-6) out[i] = vals[j] }
  return out
}
const MARKS = { "batch.start": ["B", "#94a3b8"], "batch.complete": ["C", "#94a3b8"], "fault.injected": ["!", "#ef4444"],
  "preact.trim": ["~", "#94a3b8"], "wear.rebuild_due": ["W", "#f59e0b"], "wear.rebuilt": ["R", "#10b981"], "fault.cleared": ["✓", "#10b981"] }
/** [glyph, color] for a flag-log event in a window of `span` seconds, or null when it is not drawn. */
export function flagMark(e, span) {
  // Past an hour, batch start/complete flags would cover the lane (a batch every few minutes): show only the rare flags.
  if (span > 3600 && (e.type === "batch.start" || e.type === "batch.complete")) return null
  if (e.type === "dose.verify") return e.result !== "ok" ? ["V", "#94a3b8"] : null
  if (e.type === "alarm.raised") return ["A", e.severity === "alarm" ? "#ef4444" : "#f59e0b"]
  return MARKS[e.type] || null
}
export function fmtClock(t) { const s = Math.round(t), a = Math.abs(s); const h = Math.floor(a / 3600), m = Math.floor((a % 3600) / 60), x = a % 60
  // An hour or more: hours and zero-padded minutes, signed ("-6h40", "+1h05"). Below an hour: m:ss ("-13:20").
  if (h) return `${s < 0 ? "-" : "+"}${h}h${String(m).padStart(2, "0")}`
  return `${s < 0 ? "-" : ""}${m}:${String(x).padStart(2, "0")}` }

// The trip line: dashed, labelled, drawn on the lane that carries `limit`.
function drawLimit(lane) {
  return (u) => {
    if (!lane.limit) return
    const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1
    const y = u.valToPos(lane.limit.value, "y", true), ctx = u.ctx
    ctx.save(); ctx.strokeStyle = "#ef4444"; ctx.lineWidth = dpr; ctx.setLineDash([5 * dpr, 4 * dpr])
    ctx.beginPath(); ctx.moveTo(u.bbox.left, y); ctx.lineTo(u.bbox.left + u.bbox.width, y); ctx.stroke()
    ctx.setLineDash([]); ctx.fillStyle = "#ef4444"; ctx.font = `${10 * dpr}px monospace`; ctx.textBaseline = "bottom"
    ctx.fillText(lane.limit.label, u.bbox.left + 4 * dpr, y - 2 * dpr); ctx.restore()
  }
}

/**
 * @param root    host element (children are replaced)
 * @param history createHistory() instance (needs queryMany, range)
 * @param events  host-maintained FLAG LOG, read on every render. The host passes an array containing only
 *                `batch.start`, `batch.complete`, `fault.injected`, `fault.cleared`, `preact.trim`,
 *                `wear.rebuild_due`, `wear.rebuilt`, `alarm.raised` (with its `severity`), and `dose.verify` with
 *                `result !== "ok"`, appended in time
 *                order (`t` ascending) and capped by the host. It is NOT the model's raw event stream:
 *                markers are located by binary search on `t`, so the array must be sorted.
 * @param detected the detector's `events` array (DetectedEvent[]), read on every render: spans on each event's lane,
 *                pills at windows up to an hour, and a connector through every chain of two or more in the step band.
 */
export function createTrend(root, { history, events, detected = [] }) {
  const state = { window: 900, live: true, to: 0, now: 0, lastRender: 0, dirty: false, timer: null, plots: [], listeners: new Set(),
    ov: null }
  root.innerHTML = ""
  const laneEls = LANES.map((lane) => {
    const wrap = document.createElement("div"); wrap.className = "trend-lane"; wrap.dataset.lane = lane.id
    const title = document.createElement("div"); title.className = "trend-lane-title"
    const tName = document.createElement("b"); tName.textContent = `${lane.title} (${lane.unit})`
    const tDesc = document.createElement("span"); tDesc.className = "trend-lane-desc"; tDesc.textContent = ` — ${lane.descriptor}`
    title.append(tName, tDesc)
    const plot = document.createElement("div"); wrap.append(title, plot); root.appendChild(wrap); return plot
  })
  const stepBand = document.createElement("canvas"); stepBand.className = "trend-stepband"; root.prepend(stepBand)
  const legend = document.createElement("div"); legend.className = "trend-legend"; root.prepend(legend)
  const digital = document.createElement("canvas"); digital.className = "trend-digital"; root.appendChild(digital)
  const strip = document.createElement("div"); strip.className = "trend-strip"; strip.title = "Drag to look back · click Live to follow"; root.appendChild(strip)

  const isPhone = () => typeof window !== "undefined" && window.innerWidth <= 640
  const hidden = new Set()   // legend groups switched off
  function width() { return Math.max(320, root.clientWidth) }
  function makePlot(lane, el) {
    const series = [{}]
    lane.series.forEach((s) => {
      series.push({ label: s.label, stroke: s.color, width: 1, scale: s.scale || "y", dash: s.dash, show: true })
      series.push({ label: s.label + " max", stroke: s.color, width: 1.4, scale: s.scale || "y", dash: s.dash })
    })
    lane.setpoints.forEach((_, i) => series.push({ label: `${lane.series[i].label} SP`, stroke: lane.series[i].color, width: 1, dash: [2, 3], scale: "y" }))
    const fmtTick = (u, vs) => vs.map((v) => String(v))
    const splitsOf = (ticks, range) => () => (isPhone() ? [range[0], range[1]] : ticks)
    const axisFont = "10px monospace", axisStroke = "#8ea3c0", gridStroke = "#132033"
    const opts = {
      width: width(), height: lane.height, pxAlign: 0, ms: 1,
      // Fixed ranges, identical at every window: a 2 min view and an 8 h view are the same picture at different scales.
      scales: { x: { time: false }, y: { range: () => lane.range }, kgs: { range: () => lane.range2 || lane.range } },
      axes: [
        // A compact x axis (uPlot's default reserves 50 px per lane) leaves the y axis room for the tick labels.
        { stroke: axisStroke, grid: { stroke: gridStroke }, values: (u, vs) => vs.map(fmtClock), size: 30, font: axisFont },
        // Titled in unit; splits and grid sit on the fixed ticks only.
        { stroke: axisStroke, grid: { stroke: gridStroke }, size: 34, font: axisFont, label: lane.axis, labelFont: axisFont, labelSize: 14,
          splits: splitsOf(lane.ticks, lane.range), values: fmtTick, ticks: { size: 4 } },
        // Every lane reserves the same 40 px on the right so all plot boxes (and the canvases below) share one x mapping.
        lane.id === "tank" ? { side: 1, scale: "kgs", stroke: axisStroke, grid: { show: false }, size: 40, font: axisFont, label: lane.axis2, labelFont: axisFont, labelSize: 14,
            splits: splitsOf(lane.ticks2, lane.range2), values: fmtTick, ticks: { size: 4 } }
          : { side: 1, scale: "y", size: 40, values: () => [], ticks: { show: false }, grid: { show: false }, stroke: "transparent" },
      ],
      // uPlot closes over the bands array at construction (reassigning u.bands is a no-op), and ours are static per lane.
      series, bands: laneBands(lane), legend: { show: false },
      cursor: { sync: { key: "batch", setSeries: false }, drag: { x: false, y: false }, points: { show: false } },
      hooks: { drawClear: [(u) => eventLayer && eventLayer.drawWashes(lane.id, u)], draw: [drawLimit(lane), ...(lane.id === "wt" ? [drawMarkers] : [])] },
    }
    return new uPlot(opts, [[0], ...series.slice(1).map(() => [null])], el)
  }
  let eventLayer = null   // assigned below; the drawClear hook may fire during construction
  LANES.forEach((lane, i) => state.plots.push(makePlot(lane, laneEls[i])))
  eventLayer = createEventLayer({ lanes: LANES, plots: state.plots, detected, onChange: () => paintBand(), fallbackW: () => Math.max(1, width() - 74) })

  // ---- legend: one row of chips; a chip toggles its pen pair (and setpoint) in every lane that has it ----
  const GROUPS = [
    ...STATION_DEFS.map((d) => ({ id: d.id, label: d.id, color: d.color, has: (s) => s.label === d.id })),
    { id: "scan", label: "controller scan", color: "#94a3b8", has: (s) => !!s.plc },
    { id: "TK-200.level_pct", label: "TK level", color: "#f59e0b", has: (s) => s.key === "TK-200.level_pct" },
    { id: "TK-200.flow_in", label: "TK inflow", color: "#10b981", has: (s) => s.key === "TK-200.flow_in" },
    { id: "TK-200.flow_out", label: "TK dispense", color: "#38bdf8", has: (s) => s.key === "TK-200.flow_out" },
  ]
  const groupOf = new Map(LANES.flatMap((l) => l.series).map((s) => [s, GROUPS.find((g) => g.has(s))]))   // once, not per frame
  function applyVisibility() {
    LANES.forEach((lane, li) => {
      const u = state.plots[li], n = lane.series.length
      lane.series.forEach((s, j) => {
        const g = groupOf.get(s); const show = !(g && hidden.has(g.id))
        u.setSeries(1 + 2 * j, { show }, false); u.setSeries(2 + 2 * j, { show }, false)
        if (lane.setpoints.length && j < lane.setpoints.length) u.setSeries(2 * n + 1 + j, { show }, false)
      })
    })
    legend.querySelectorAll(".trend-chip").forEach((c) => c.classList.toggle("off", hidden.has(c.dataset.group)))
    trend.render(state.now, true)
  }
  GROUPS.forEach((g) => {
    const c = document.createElement("button"); c.type = "button"; c.className = "trend-chip"; c.dataset.group = g.id
    c.title = `Show / hide ${g.label}`
    const sw = document.createElement("i"); sw.style.background = g.color
    c.append(sw, document.createTextNode(g.label))
    c.addEventListener("click", () => { hidden.has(g.id) ? hidden.delete(g.id) : hidden.add(g.id); applyVisibility() })
    legend.appendChild(c)
  })

  // ---- right-edge value tags: an HTML layer inside each plot box, one tag per visible pen ----
  const TAG_H = 12
  const tagLayers = LANES.map((lane, li) => {
    const layer = document.createElement("div"); layer.className = "trend-tags"; state.plots[li].over.appendChild(layer)
    const tags = lane.series.filter((s) => !s.plc).map((s) => {
      const el = document.createElement("div"); el.className = "trend-tag"; el.style.color = s.color
      const id = document.createElement("span"); id.className = "tid"; id.textContent = s.label + " "
      const v = document.createElement("span"); v.className = "tv"
      el.append(id, v); layer.appendChild(el)
      return { s, el, v, group: groupOf.get(s), j: lane.series.indexOf(s), text: null, top: null, shown: null }
    })
    return { layer, tags }
  })
  // Two phases so layout reads never interleave with style writes: measure every lane, then write.
  function updateAllTags(laneData) {
    const plans = LANES.map((lane, li) => {
      const u = state.plots[li], H = u.over.clientHeight, live = []
      tagLayers[li].tags.forEach((t) => {
        const col = laneData[li][2 + 2 * t.j]; let val = null
        for (let k = col.length - 1; k >= 0; k--) if (col[k] != null) { val = col[k]; break }
        if (val == null || (t.group && hidden.has(t.group.id))) { live.push({ t, hide: true }); return }
        live.push({ t, val, want: (H - TAG_H) - (u.valToPos(val, t.s.scale || "y") - TAG_H / 2) })
      })
      const shown = live.filter((l) => !l.hide)
      const tops = tagLayout(shown.map((l) => l.want), H - TAG_H, TAG_H)
      shown.forEach((l, k) => { l.top = Math.round(tops[k]) })
      return { lane, live }
    })
    for (const { lane, live } of plans) for (const l of live) {
      const t = l.t
      if (l.hide) { if (t.shown !== false) { t.el.style.display = "none"; t.shown = false } continue }
      if (t.shown !== true) { t.el.style.display = ""; t.shown = true }
      const text = l.val.toFixed(lane.dec); if (text !== t.text) { t.v.textContent = text; t.text = text }
      if (l.top !== t.top) { t.el.style.top = `${l.top}px`; t.top = l.top }
    }
  }

  function view() { const to = state.live ? state.now : state.to; return { from: to - state.window, to, live: state.live, window: state.window } }
  function notify() { for (const fn of state.listeners) fn(view()) }

  function drawMarkers(u) {
    const { from, to } = view()
    const dens = densityFor(to - from)
    // First flag with t >= from (events are time-ordered), then stop at `to`.
    let lo = 0, hi = events.length
    while (lo < hi) { const m = (lo + hi) >> 1; if (events[m].t < from) lo = m + 1; else hi = m }
    const ctx = u.ctx; ctx.save()
    for (let n = lo; n < events.length && events[n].t <= to; n++) {
      const e = events[n]
      const mk = flagMark(e, to - from)
      if (!mk) continue
      if (!dens.batchMarkers && (e.type === "batch.start" || e.type === "batch.complete")) continue
      const x = u.valToPos(e.t, "x", true)
      ctx.strokeStyle = mk[1]; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(x, u.bbox.top); ctx.lineTo(x, u.bbox.top + u.bbox.height); ctx.stroke()
      ctx.fillStyle = mk[1]; ctx.font = "10px monospace"; ctx.fillText(mk[0], x + 2, u.bbox.top + 10)
    }
    ctx.restore()
  }

  // One shared time->x mapping for the canvases, from lane 0's real plot box (CSS px). Read once per render.
  function plotBox(from, to) {
    const over = state.plots[0].over
    const left = over.offsetLeft, w = Math.max(1, over.clientWidth)
    return { left, w, xOf: (t) => left + ((t - from) / (to - from)) * w }
  }

  // Backing store at devicePixelRatio (crisp on HiDPI); drawing stays in CSS px, the CSS width stays 100 %.
  function sizeCanvas(c, W, H) {
    const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1
    c.width = Math.round(W * dpr); c.height = Math.round(H * dpr); c.style.height = `${H}px`
    const ctx = c.getContext("2d"); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H)
    return ctx
  }
  function drawDigital(from, to, box) {
    const W = width(), dens = densityFor(to - from), rowH = 10, per = dens.perValveRows ? 3 : 1
    const ctx = sizeCanvas(digital, W, STATION_DEFS.length * per * rowH + 4)
    const keys = ["xv01", "xv02", "mtr_run", "xv03"]
    const idx = STATION_DEFS.flatMap((d) => keys.map((k) => SIGNAL_INDEX[`${d.id}.${k}`]))
    const qs = history.queryMany(idx, from, to, box.w)   // one pass for all 24 digital signals
    // per-valve: three rows per station. Combined: one row, the three signals as thin stripes inside it.
    const slot = (si, r) => dens.perValveRows ? { y: (si * 3 + r) * rowH, h: rowH - 2 } : { y: si * rowH + r * 3, h: 2 }
    STATION_DEFS.forEach((d, si) => {
      const [xv01, xv02, mtr, out] = qs.slice(si * 4, si * 4 + 4)
      const bw = (n) => Math.max(1, box.w / n)
      for (let i = 0; i < xv01.ts.length; i++) {
        const s = valveState(xv01.max[i], xv02.max[i]), r = slot(si, 0)
        if (s) { ctx.fillStyle = s === 2 ? "#0ea5e9" : "#f59e0b"; ctx.fillRect(box.xOf(xv01.ts[i]), r.y, bw(xv01.ts.length), r.h) }
      }
      for (let i = 0; i < mtr.ts.length; i++) if (mtr.max[i]) { const r = slot(si, 1); ctx.fillStyle = "#8b5cf6"; ctx.fillRect(box.xOf(mtr.ts[i]), r.y, bw(mtr.ts.length), r.h) }
      for (let i = 0; i < out.ts.length; i++) if (out.max[i]) { const r = slot(si, 2); ctx.fillStyle = "#10b981"; ctx.fillRect(box.xOf(out.ts[i]), r.y, bw(out.ts.length), r.h) }
      ctx.fillStyle = "#8ea3c0"; ctx.font = "9px monospace"; ctx.fillText(d.id, 2, si * per * rowH + 8)
    })
  }
  // Step band: phase names appear only while the pointer is on the band (one mousemove listener), drawn at the cursor.
  const band = { qs: null, from: 0, to: 1, box: null, hover: null }
  function drawStepBand(from, to, box) {
    band.qs = history.queryMany(STATION_DEFS.map((d) => SIGNAL_INDEX[`${d.id}.phase`]), from, to, box.w)
    band.from = from; band.to = to; band.box = box
    paintBand()
  }
  function paintBand() {
    const { qs, box } = band
    if (!qs) return
    const W = width(), rowH = 8
    const ctx = sizeCanvas(stepBand, W, STATION_DEFS.length * rowH + 2)
    STATION_DEFS.forEach((d, si) => {
      const q = qs[si]
      for (let i = 0; i < q.ts.length; i++) { ctx.fillStyle = PHASE_COLORS[PHASES[Math.round(q.last[i])]] || "#000"; ctx.fillRect(box.xOf(q.ts[i]), si * rowH, Math.max(1, box.w / q.ts.length), rowH - 1) }
      // Row label in the left margin (the plot box starts after the y axis), as the digital canvas does.
      ctx.fillStyle = "#8ea3c0"; ctx.font = "7px monospace"; ctx.fillText(d.id, 2, si * rowH + 6.5)
    })
    eventLayer.paintChains(ctx, box)
    const h = band.hover
    if (h) {
      const si = Math.min(STATION_DEFS.length - 1, Math.max(0, Math.floor(h.y / rowH))), q = qs[si]
      const t = band.from + ((h.x - box.left) / box.w) * (band.to - band.from)
      let k = -1; for (let i = 0; i < q.ts.length && q.ts[i] <= t; i++) k = i
      const name = k >= 0 ? PHASES[Math.round(q.last[k])] : null
      if (name) {
        const txt = `${STATION_DEFS[si].id} ${name}`; ctx.font = "10px monospace"
        const tw = ctx.measureText(txt).width + 8, x = Math.min(W - tw - 2, Math.max(2, h.x + 8))
        ctx.fillStyle = "rgba(5,9,17,.92)"; ctx.fillRect(x, 0, tw, 14); ctx.strokeStyle = "#334155"; ctx.strokeRect(x + .5, .5, tw - 1, 13)
        ctx.fillStyle = "#e2e8f0"; ctx.fillText(txt, x + 4, 10.5)
      }
    }
  }
  const hover = { x: 0, y: 0 }; let bandFrame = 0
  const repaintBand = () => { if (!bandFrame) bandFrame = requestAnimationFrame(() => { bandFrame = 0; paintBand() }) }   // one pending frame
  stepBand.addEventListener("mousemove", (e) => { const r = stepBand.getBoundingClientRect(); hover.x = e.clientX - r.left; hover.y = e.clientY - r.top; band.hover = hover; repaintBand() })
  stepBand.addEventListener("mouseleave", () => { band.hover = null; repaintBand() })

  // Scrub: drag on the strip pans; wheel pans; chips/Live are the host's buttons calling setWindow/setLive.
  let dragX = null
  strip.addEventListener("pointerdown", (e) => { dragX = e.clientX; strip.setPointerCapture(e.pointerId) })
  strip.addEventListener("pointermove", (e) => { if (dragX == null) return; const dx = e.clientX - dragX; dragX = e.clientX; trend.pan(-(dx / width()) * state.window) })
  strip.addEventListener("pointerup", () => { dragX = null })
  strip.addEventListener("wheel", (e) => { e.preventDefault(); trend.pan(Math.sign(e.deltaY) * state.window * 0.1) }, { passive: false })

  // Interaction requests a redraw; render() itself never runs more often than RENDER_MS_INTERACT.
  function requestRedraw() {
    state.dirty = true
    if (state.timer == null) state.timer = setTimeout(() => { state.timer = null; trend.render(state.now) }, RENDER_MS_INTERACT)
  }
  const trend = {
    setWindow(s) { state.window = s; requestRedraw(); notify() },
    setLive(on) { if (state.live) state.to = state.now; state.live = !!on; if (on) state.to = state.now; requestRedraw(); notify() },
    pan(ds) { if (state.live) { state.live = false; state.to = state.now } state.to = Math.min(state.now, Math.max(history.range().from + state.window, state.to + ds)); requestRedraw(); notify() },
    onEventClick(fn) { return eventLayer.onEventClick(fn) },
    highlightEvent(id) { eventLayer.highlightEvent(id) },
    view, onView(fn) { state.listeners.add(fn); return () => state.listeners.delete(fn) },
    render(nowT, force = false) {
      state.now = nowT
      const wall = performance.now()
      if (!force && wall - state.lastRender < (state.dirty ? RENDER_MS_INTERACT : RENDER_MS)) return
      state.lastRender = wall; state.dirty = false
      const { from, to } = view()
      const px = width()
      const dims = eventLayer.measure()          // every layout read first, before uPlot or the layer write anything
      eventLayer.setView(from, to, dims)         // before uPlot commits: its drawClear hook paints this view's washes
      const laneData = []
      LANES.forEach((lane, i) => {
        const { data } = buildLaneData(history, lane, from, to, px)
        laneData[i] = data
        const u = state.plots[i]
        if (u.width !== px) u.setSize({ width: px, height: lane.height })
        u.setData(data, false)
        u.setScale("x", { min: from, max: to })
      })
      // uPlot applies setSize/setData to the DOM in a microtask, so the plot box is only valid after it runs:
      // draw the canvases then (queued after uPlot's own commit), from the box of lane 0.
      queueMicrotask(() => {
        if (state.destroyed) return
        const box = plotBox(from, to)
        drawStepBand(from, to, box); drawDigital(from, to, box); eventLayer.draw()
        updateAllTags(laneData)
      })
      strip.style.setProperty("--pos", state.live ? "100%" : `${(100 * (to - history.range().from)) / Math.max(1, nowT - history.range().from)}%`)
    },
    destroy() { state.destroyed = true; eventLayer.destroy(); if (state.timer != null) clearTimeout(state.timer); state.plots.forEach((p) => p.destroy()) },
  }
  return trend
}

/**
 * A second, independent live chart over the same history/detected for the lens: the requested lanes (1 or 2) over the last
 * `window` seconds ending at "now", re-fed at 4 Hz while it exists. `emphasize` (a station id) draws that station's pens at
 * 2.5 px and every other pen at 1 px, 40 % opacity. Detected-event washes, bars and pills come from a second event layer.
 * Native size, no CSS scaling (a scaled canvas blurs). `destroy()` stops the timer and the plots.
 */
export function createTrendLens(root, { history, detected = [], lanes: laneIds = ["wt"], window: windowS = 900, emphasize = null, now = null }) {
  const lanes = laneIds.map((id) => LANES.find((l) => l.id === id)).filter(Boolean).slice(0, 2)
  const st = { destroyed: false, timer: null, window: windowS, emphasize }
  root.innerHTML = ""
  const nowT = () => (now ? now() : history.range().to)
  const width = () => Math.max(240, root.clientWidth || 480)
  const plotEls = lanes.map((lane) => {
    const wrap = document.createElement("div"); wrap.className = "trend-lane"; wrap.dataset.lane = lane.id
    const title = document.createElement("div"); title.className = "trend-lane-title"
    const b = document.createElement("b"); b.textContent = `${lane.title} (${lane.unit})`
    title.appendChild(b)
    const keys = document.createElement("span"); keys.className = "tl-keys"
    for (const s of lane.series.filter((x) => !x.plc)) {
      const c = document.createElement("span"); c.className = "tl-key" + (emphasize && s.label === emphasize ? " on" : ""); c.style.color = s.color
      c.textContent = s.label; keys.appendChild(c)
    }
    title.appendChild(keys)
    const plot = document.createElement("div"); wrap.append(title, plot); root.appendChild(wrap); return plot
  })
  const dim = (s) => !!emphasize && lanes.some((l) => l.series.some((x) => x.label === emphasize)) && s.label !== emphasize
  // uPlot paints series in index order: the emphasized station goes last so its pen sits on top of the crowd.
  // order[k] = the lane.series index drawn k-th; the data columns are permuted the same way in render().
  const orderOf = (lane) => { const o = lane.series.map((_, i) => i); return emphasize ? [...o.filter((i) => lane.series[i].label !== emphasize), ...o.filter((i) => lane.series[i].label === emphasize)] : o }
  const makePlot = (lane, el) => {
    const series = [{}], ord = orderOf(lane), ser = ord.map((i) => lane.series[i])
    ser.forEach((s) => {
      const on = emphasize && s.label === emphasize, off = dim(s) && !s.plc
      const stroke = off ? hex2rgba(s.color, 0.4) : s.color, w = on ? 2.5 : off ? 1 : 1, wMax = on ? 2.5 : off ? 1 : 1.4
      series.push({ label: s.label, stroke, width: w, scale: s.scale || "y", dash: s.dash, show: true })
      series.push({ label: s.label + " max", stroke, width: wMax, scale: s.scale || "y", dash: s.dash })
    })
    ord.forEach((i) => { if (i < lane.setpoints.length) series.push({ label: `${lane.series[i].label} SP`, stroke: dim(lane.series[i]) ? hex2rgba(lane.series[i].color, 0.4) : lane.series[i].color, width: 1, dash: [2, 3], scale: "y" }) })
    const axisFont = "10px monospace", axisStroke = "#8ea3c0", gridStroke = "#132033"
    const bands = ser.map((s, i) => ({ series: [2 + i * 2, 1 + i * 2], fill: hex2rgba(s.color, dim(s) ? 0.05 : 0.22) }))
    return new uPlot({
      width: width(), height: lane.height, pxAlign: 0, ms: 1,
      scales: { x: { time: false }, y: { range: () => lane.range }, kgs: { range: () => lane.range2 || lane.range } },
      axes: [
        { stroke: axisStroke, grid: { stroke: gridStroke }, values: (u, vs) => vs.map(fmtClock), size: 30, font: axisFont },
        { stroke: axisStroke, grid: { stroke: gridStroke }, size: 34, font: axisFont, label: lane.axis, labelFont: axisFont, labelSize: 14, splits: () => lane.ticks, values: (u, vs) => vs.map(String), ticks: { size: 4 } },
        lane.id === "tank" ? { side: 1, scale: "kgs", stroke: axisStroke, grid: { show: false }, size: 40, font: axisFont, label: lane.axis2, labelFont: axisFont, labelSize: 14, splits: () => lane.ticks2, values: (u, vs) => vs.map(String), ticks: { size: 4 } }
          : { side: 1, scale: "y", size: 40, values: () => [], ticks: { show: false }, grid: { show: false }, stroke: "transparent" },
      ],
      series, bands, legend: { show: false }, cursor: { show: false, drag: { x: false, y: false }, points: { show: false } },
      hooks: { drawClear: [(u) => layer && layer.drawWashes(lane.id, u)], draw: [drawLimit(lane)] },
    }, [[0], ...series.slice(1).map(() => [null])], el)
  }
  let layer = null
  const orders = lanes.map(orderOf)
  const plots = lanes.map((lane, i) => makePlot(lane, plotEls[i]))
  layer = createEventLayer({ lanes, plots, detected, onChange: () => {}, fallbackW: () => Math.max(1, width() - 74) })

  function render() {
    if (st.destroyed) return
    const to = nowT(), from = to - st.window, px = width()
    const dims = layer.measure()
    layer.setView(from, to, dims)
    lanes.forEach((lane, i) => {
      const { data: raw } = buildLaneData(history, lane, from, to, px), u = plots[i], ord = orders[i], n = lane.series.length
      const data = [raw[0], ...ord.flatMap((k) => [raw[1 + 2 * k], raw[2 + 2 * k]]), ...ord.filter((k) => k < lane.setpoints.length).map((k) => raw[1 + 2 * n + k])]
      if (u.width !== px) u.setSize({ width: px, height: lane.height })
      u.setData(data, false)
      u.setScale("x", { min: from, max: to })
    })
    queueMicrotask(() => { if (!st.destroyed) layer.draw() })
  }
  render()
  st.timer = setInterval(render, 250)
  return {
    render,
    setWindow(s) { st.window = s; render() },
    destroy() { if (st.destroyed) return; st.destroyed = true; clearInterval(st.timer); st.timer = null; layer.destroy(); plots.forEach((p) => p.destroy()); root.innerHTML = "" },
  }
}
